/**
 * Повний редактор вихідної ПН (рішення власника 2026-07-28: «нужны
 * нормальные страницы создания ПН, в 1С функциональнее» — замість
 * read-only viewer'а + модалки «з реалізації»).
 *
 * - Створення вручну АБО «заповнити з реалізації» (попап проведених).
 * - Таблична частина: номенклатура (комбобокс) / опис / УКТЗЕД / к-сть /
 *   ціна без ПДВ / ставка — база й ПДВ рахуються (сервер перерахує так само).
 * - «Не видається покупцю» з ОФІЦІЙНИМ переліком причин 01–22 (XSD ДПС).
 * - Розкладка за ставками — авто; блок ЄРПН веде у Кабінет ДПС (єдина
 *   механіка подання: підпис КЕП і відправка — у Журналі Кабінету).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import { bankReceipts } from '@/api/bankDocs';
import { cashReceipts } from '@/api/cashDocs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taxInvoicesOutgoing,
  type TaxInvoiceItemIn,
} from '@/api/taxInvoices';
import { goodsSales } from '@/api/goodsSales';
import { productionServiceActs } from '@/api/productionServiceActs';
import { processingServicesSales } from '@/api/processingServicesSales';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import SearchSelect from '@/components/SearchSelect';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import ErpnAppealsCard from '@/shared/components/ErpnAppealsCard';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const fmt = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Офіційний перелік типів причини «не видається покупцю» (DGPNtypr,
// common_types.xsd ДПС, наказ №256 від 17.05.2023).
export const NOT_ISSUED_REASONS: [string, string][] = [
  ['01', 'Перевищення звичайної ціни над фактичною'],
  ['02', 'Постачання неплатнику податку'],
  ['03', 'Натуральна виплата в рахунок оплати праці'],
  ['04', 'Постачання у межах балансу (невиробниче використання)'],
  ['05', 'Ліквідація основних засобів'],
  ['06', 'Переведення виробничих ОЗ до невиробничих'],
  ['07', 'Експортні постачання'],
  ['08', 'Операції, що не є об’єктом оподаткування'],
  ['09', 'Операції, звільнені від оподаткування'],
  ['10', 'Умовне постачання при анулюванні реєстрації'],
  ['11', 'За щоденними підсумками операцій'],
  ['12', 'Безоплатне постачання (звичайні ціни)'],
  ['13', 'Використання не у господарській діяльності'],
  ['14', 'Отримання послуг від нерезидента'],
  ['15', 'Перевищення ціни придбання над ціною постачання'],
  ['16', 'Перевищення балансової вартості НА над ціною постачання'],
  ['17', 'Перевищення собівартості над ціною постачання'],
  ['21', 'Послуги нерезиденту з місцем постачання в Україні'],
  ['22', 'Експорт у режимі експортного забезпечення'],
];

interface Row extends TaxInvoiceItemIn {
  /** Локальний ключ рядка для React. */
  _key: string;
}

interface FormState {
  organization_id: string;
  invoice_number: string;
  invoice_date: string;          // YYYY-MM-DD
  contractor_id: string;
  contract_id: string;
  not_issued: boolean;
  not_issued_reason: string;
  comment: string;  // ч.889
  base_sale_id: string;
  // ч.1447: ПН на аванс — підстава надходження (банк / каса).
  base_doc_type: 'doc.goods_sales' | 'doc.production_service_acts' | 'doc.processing_services_sales'
    | 'doc.bank_receipts' | 'doc.cash_receipts';
  items: Row[];
}

let _rowSeq = 0;
const newKey = () => `r${++_rowSeq}`;

const BASE_TYPES: FormState['base_doc_type'][] = [
  'doc.goods_sales', 'doc.production_service_acts', 'doc.processing_services_sales',
  'doc.bank_receipts', 'doc.cash_receipts',
];
const isReceiptBase = (t: FormState['base_doc_type']) => t === 'doc.bank_receipts' || t === 'doc.cash_receipts';

const EMPTY: FormState = {
  organization_id: '',
  invoice_number: '',
  invoice_date: new Date().toISOString().slice(0, 10),
  contractor_id: '',
  contract_id: '',
  not_issued: false,
  not_issued_reason: '02',
  comment: '',
  base_sale_id: '',
  base_doc_type: 'doc.goods_sales',
  items: [],
};

export default function TaxInvoiceOutgoingEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [f, setF] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [salePopup, setSalePopup] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [erpnBusy, setErpnBusy] = useState(false);
  const [erpnError, setErpnError] = useState<string | null>(null);
  const [erpnWarn, setErpnWarn] = useState<string | null>(null);
  // ч.898: ручна позначка «зареєстровано в ЄРПН» (попап з датою).
  const [markOpen, setMarkOpen] = useState(false);
  const [markDate, setMarkDate] = useState(new Date().toISOString().slice(0, 10));
  const [markBusy, setMarkBusy] = useState(false);
  // ч.900: вкладки рядків як у 1С (Товари / Послуги / ОЗ і НМА).
  const [linesTab, setLinesTab] = useState<'GOODS' | 'SERVICE' | 'ASSET'>('GOODS');
  const rowTab = (r: Row): 'GOODS' | 'SERVICE' | 'ASSET' =>
    r.row_kind === 'SERVICE' ? 'SERVICE' : (r.row_kind === 'FA' || r.row_kind === 'IA') ? 'ASSET' : 'GOODS';

  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('tax.invoice.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', f.organization_id, f.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: f.organization_id, contractor_id: f.contractor_id,
    }),
    enabled: !!f.organization_id && !!f.contractor_id,
  });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });

  const docQ = useQuery({
    queryKey: ['tax-invoice-outgoing', id],
    queryFn: () => taxInvoicesOutgoing.get(id!),
    enabled: !isNew,
  });
  // ч.1352: виправлювана ПН (для РК) — хук ДО ранніх return (правила хуків).
  const parentPnQ = useQuery({
    queryKey: ['tax-invoice-outgoing', docQ.data?.corrected_invoice_id],
    queryFn: () => taxInvoicesOutgoing.get(docQ.data!.corrected_invoice_id!),
    enabled: !!docQ.data?.corrected_invoice_id,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(f, setF, { ready: isNew || !!docQ.data });
  const d = docQ.data;

  useEffect(() => {
    if (!d) return;
    setF({
      organization_id: d.organization_id,
      invoice_number: d.invoice_number,
      invoice_date: d.invoice_date,
      contractor_id: d.contractor_id,
      contract_id: d.contract_id ?? '',
      not_issued: (d as any).not_issued ?? false,
      not_issued_reason: (d as any).not_issued_reason ?? '02',
      comment: d.comment ?? '',
      base_sale_id: (d.base_document_type && BASE_TYPES.includes(d.base_document_type as FormState['base_doc_type']))
        ? (d.base_document_id ?? '') : '',
      base_doc_type: (d.base_document_type && BASE_TYPES.includes(d.base_document_type as FormState['base_doc_type']))
        ? d.base_document_type as FormState['base_doc_type'] : 'doc.goods_sales',
      items: d.items.map((it) => ({
        _key: newKey(),
        nomenclature_id: it.nomenclature_id,
        description: it.description,
        ukt_zed_code: it.ukt_zed_code,
        uom_code: it.uom_code,
        quantity: it.quantity,
        price_without_vat: it.price_without_vat,
        vat_rate_id: it.vat_rate_id,
        base_amount: it.base_amount,
        vat_amount: it.vat_amount,
        row_kind: (it.row_kind as Row['row_kind']) ?? 'GOODS',
      })),
    });
  }, [d]);

  useEffect(() => {
    if (!isNew || f.organization_id || !availableOrgs.length) return;
    const preferred = availableOrgs.find((o) => o.id === defaultOrgId) ?? availableOrgs[0];
    if (preferred) setF((s) => ({ ...s, organization_id: preferred.id }));
  }, [isNew, availableOrgs, defaultOrgId, f.organization_id]);

  const nomById = useMemo(() => {
    const m = new Map<string, { name: string; ukt?: string | null; isService?: boolean }>();
    nomListQ.data?.items.forEach((n) => m.set(n.id, { name: n.name, ukt: n.ukt_zed_code, isService: !!n.is_service }));
    return m;
  }, [nomListQ.data]);

  const buyer = contractorsQ.data?.items.find((c) => c.id === f.contractor_id);
  // Підстава (реалізація): номер для показу + посилання.
  const baseSaleQ = useQuery({
    queryKey: ['goods-sale-brief', f.base_sale_id],
    queryFn: () => goodsSales.get(f.base_sale_id),
    enabled: !!f.base_sale_id && f.base_doc_type === 'doc.goods_sales',
  });
  // ч.1447: ПН на аванс з надходження — номер і дата оплати для показу.
  const baseReceiptQ = useQuery({
    queryKey: ['receipt-brief', f.base_doc_type, f.base_sale_id],
    queryFn: async (): Promise<{ document_number: string; document_date: string }> => (
      f.base_doc_type === 'doc.bank_receipts' ? bankReceipts.get(f.base_sale_id) : cashReceipts.get(f.base_sale_id)),
    enabled: !!f.base_sale_id && isReceiptBase(f.base_doc_type),
  });
  const rates = vatRatesQ.data ?? [];
  const rate20 = rates.find((r) => r.code === 'VAT_20');
  const rateById = useMemo(() => {
    const m = new Map<string, { code: string; pct: number }>();
    rates.forEach((r) => m.set(r.id, { code: r.code, pct: Number(r.rate_percent) }));
    return m;
  }, [rates]);

  // Розрахунок рядка/підсумків — дзеркало серверної логіки.
  function lineAmounts(r: Row): { base: number; vat: number } {
    // ч.891: підсумковий рядок (кількість 0 — так 1С УТП зберігає вхідну ПН:
    // лише суми) — база/ПДВ з рядка, а не кількість × ціна.
    const q = Number(r.quantity) || 0;
    const rate = rateById.get(r.vat_rate_id);
    const taxable = !!rate && ['VAT_20', 'VAT_7', 'VAT_14'].includes(rate.code);
    if (q === 0 && r.base_amount != null && r.base_amount !== '') {
      const base = Math.round(Number(r.base_amount) * 100) / 100;
      const vat = r.vat_amount != null && r.vat_amount !== ''
        ? Math.round(Number(r.vat_amount) * 100) / 100
        : (taxable ? Math.round(base * rate!.pct) / 100 : 0);
      return { base, vat };
    }
    const base = Math.round(q * (Number(r.price_without_vat) || 0) * 100) / 100;
    const vat = taxable ? Math.round(base * rate!.pct) / 100 : 0;
    return { base, vat };
  }
  const totals = useMemo(() => {
    const t = { b20: 0, v20: 0, b7: 0, v7: 0, b14: 0, v14: 0, b0: 0, bex: 0 };
    for (const r of f.items) {
      const { base, vat } = lineAmounts(r);
      const rate = rateById.get(r.vat_rate_id);
      if (!rate) continue;
      if (rate.pct === 20) { t.b20 += base; t.v20 += vat; }
      else if (rate.pct === 7) { t.b7 += base; t.v7 += vat; }
      else if (rate.pct === 14) { t.b14 += base; t.v14 += vat; }
      else if (rate.code === 'VAT_0') t.b0 += base;
      else t.bex += base;
    }
    return { ...t, total: t.b20 + t.v20 + t.b7 + t.v7 + t.b14 + t.v14 + t.b0 + t.bex };
  }, [f.items, rateById]);

  // «Заповнити з реалізації»: проведені реалізації (фільтр по покупцю).
  const salesQ = useQuery({
    queryKey: ['gs-posted', f.organization_id, f.contractor_id],
    queryFn: () => goodsSales.list({
      organization_id: f.organization_id || undefined,
      contractor_id: f.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: salePopup,
  });

  const actsQ = useQuery({
    queryKey: ['psa-posted', f.organization_id, f.contractor_id],
    queryFn: () => productionServiceActs.list({
      organization_id: f.organization_id || undefined,
      customer_contractor_id: f.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: salePopup,
  });
  const pssQ = useQuery({
    queryKey: ['pss-posted', f.organization_id, f.contractor_id],
    queryFn: () => processingServicesSales.list({
      organization_id: f.organization_id || undefined,
      customer_contractor_id: f.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: salePopup,
  });
  // ч.697: ставка за відсотком (ефективна ставка актів/рядкова послуг).
  const rateIdByPct = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rates) {
      const pct = Number(r.rate_percent);
      if (!m.has(pct) || r.code === 'VAT_0') m.set(pct, r.id);
    }
    return m;
  }, [rates]);

  function effPct(base: number, vat: number): number {
    if (base <= 0) return vat > 0 ? 20 : 0;
    const ratio = vat / base * 100;
    for (const cand of [20, 14, 7, 0]) {
      if (Math.abs(ratio - cand) < 0.5) return cand;
    }
    return vat > 0 ? 20 : 0;
  }

  async function fillFromAct(actId: string) {
    try {
      const act = await productionServiceActs.get(actId);
      const pct = effPct(Number(act.service_amount), Number(act.vat_amount));
      const rid = rateIdByPct.get(pct) ?? rate20?.id ?? '';
      setF((s) => ({
        ...s,
        organization_id: act.organization_id,
        contractor_id: act.customer_contractor_id,
        contract_id: act.contract_id ?? '',
        base_sale_id: actId,
        base_doc_type: 'doc.production_service_acts',
        invoice_date: (act.document_date ?? s.invoice_date).slice(0, 10),
        items: act.items.map((it) => ({
          _key: newKey(),
          nomenclature_id: it.nomenclature_id,
          description: nomById.get(it.nomenclature_id)?.name ?? '',
          ukt_zed_code: nomById.get(it.nomenclature_id)?.ukt ?? null,
          uom_code: null,
          quantity: String(it.quantity),
          price_without_vat: String(it.price),
          vat_rate_id: rid,
        })),
      }));
      setSalePopup(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  }

  async function fillFromPss(docId: string) {
    try {
      const doc = await processingServicesSales.get(docId);
      const svc = (doc.services ?? []).filter((x) => x.nomenclature_id);
      if (svc.length === 0) {
        setError('У документа немає рядків послуг з номенклатурою — ' +
          'додайте їх на вкладці «Послуги» (ПН вимагає номенклатуру).');
        return;
      }
      const effDoc = effPct(Number(doc.service_amount), Number(doc.vat_amount));
      setF((s) => ({
        ...s,
        organization_id: doc.organization_id,
        contractor_id: doc.customer_contractor_id,
        contract_id: doc.contract_id ?? '',
        base_sale_id: docId,
        base_doc_type: 'doc.processing_services_sales',
        invoice_date: (doc.document_date ?? s.invoice_date).slice(0, 10),
        items: svc.map((it) => {
          const pct = it.vat_rate_percent != null && it.vat_rate_percent !== ''
            ? Number(it.vat_rate_percent) : effDoc;
          return {
            _key: newKey(),
            nomenclature_id: it.nomenclature_id!,
            description: it.description
              || nomById.get(it.nomenclature_id!)?.name || '',
            ukt_zed_code: nomById.get(it.nomenclature_id!)?.ukt ?? null,
            uom_code: null,
            quantity: String(it.quantity ?? '1'),
            price_without_vat: String(it.price ?? it.amount ?? '0'),
            vat_rate_id: rateIdByPct.get(pct) ?? rate20?.id ?? '',
          };
        }),
      }));
      setSalePopup(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  }

  async function fillFromSale(saleId: string) {
    try {
      const sale = await goodsSales.get(saleId);
      setF((s) => ({
        ...s,
        organization_id: sale.organization_id,
        contractor_id: sale.contractor_id,
        contract_id: sale.contract_id ?? '',
        base_sale_id: saleId,
        base_doc_type: 'doc.goods_sales',
        invoice_date: (sale.document_date ?? s.invoice_date).slice(0, 10),
        items: sale.items.map((it: any) => ({
          _key: newKey(),
          nomenclature_id: it.nomenclature_id,
          description: nomById.get(it.nomenclature_id)?.name ?? '',
          ukt_zed_code: nomById.get(it.nomenclature_id)?.ukt ?? null,
          uom_code: null,
          quantity: String(it.quantity),
          price_without_vat: String(it.price),
          vat_rate_id: it.vat_rate_id,
          // ч.906: вкладка ПН — послуга → «Послуги», власний ОЗ → «ОЗ», решта
          // (товари і «Обладнання» реалізації) → «Товари».
          row_kind: it.fixed_asset_id ? 'FA'
            : (nomById.get(it.nomenclature_id)?.isService ? 'SERVICE' : 'GOODS'),
        })),
      }));
      setSalePopup(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  }

  function setRow(key: string, patch: Partial<Row>) {
    setF((s) => ({
      ...s,
      items: s.items.map((r) => (r._key === key ? { ...r, ...patch } : r)),
    }));
  }

  // Ставка ПДВ з картки номенклатури (як у рахунку/приході).
  const vatRateByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomListQ.data?.items ?? []) {
      if ((n as { vat_rate_id?: string | null }).vat_rate_id) {
        m.set(n.id, (n as { vat_rate_id?: string }).vat_rate_id!);
      }
    }
    return m;
  }, [nomListQ.data]);

  function addRowFromPicker(picked: GoodsBalanceRow) {
    const info = nomById.get(picked.nomenclature_id);
    setF((s) => ({
      ...s,
      items: [...s.items, {
        _key: newKey(),
        nomenclature_id: picked.nomenclature_id,
        description: info?.name ?? '',
        ukt_zed_code: info?.ukt ?? null,
        uom_code: null,
        quantity: '1',
        price_without_vat: '0',
        vat_rate_id: vatRateByNomId.get(picked.nomenclature_id)
          ?? rate20?.id ?? '',
        // ч.900: послуга → вкладка «Послуги»; інакше — поточна вкладка.
        row_kind: info?.isService ? 'SERVICE' : (linesTab === 'ASSET' ? 'FA' : linesTab),
      }],
    }));
    setPickerOpen(false);
  }

  function addRow() {
    setF((s) => ({
      ...s,
      items: [...s.items, {
        _key: newKey(), nomenclature_id: null, description: '',
        ukt_zed_code: null, uom_code: null, quantity: '1',
        price_without_vat: '0', vat_rate_id: rate20?.id ?? '',
        row_kind: linesTab === 'ASSET' ? 'FA' : linesTab,
      }],
    }));
  }

  function buildItems(): TaxInvoiceItemIn[] {
    const items = f.items
      .filter((r) => r.description.trim() || r.nomenclature_id)
      .map(({ _key, ...r }) => ({
        ...r,
        description: r.description.trim()
          || (r.nomenclature_id ? (nomById.get(r.nomenclature_id)?.name ?? '—') : '—'),
      }));
    if (!items.length) throw new Error('Додайте хоча б один рядок');
    for (const [i, it] of items.entries()) {
      if (!(Number(it.quantity) > 0) && !(Number(it.base_amount) > 0)) {
        throw new Error(`Рядок ${i + 1}: вкажіть кількість або суму рядка`);
      }
      if (!it.vat_rate_id) throw new Error(`Рядок ${i + 1}: оберіть ставку ПДВ`);
    }
    return items;
  }

  function buildPayload() {
    if (!f.organization_id) throw new Error('Оберіть організацію');
    if (!f.contractor_id) throw new Error('Оберіть покупця');
    return {
      organization_id: f.organization_id,
      invoice_number: f.invoice_number || undefined,
      invoice_date: f.invoice_date,
      contractor_id: f.contractor_id,
      contract_id: f.contract_id || null,
      not_issued: f.not_issued,
      not_issued_reason: f.not_issued ? f.not_issued_reason : null,
      comment: f.comment.trim() || null,
      base_document_type: f.base_sale_id ? f.base_doc_type : null,
      base_document_id: f.base_sale_id || null,
      items: buildItems(),
    };
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tax-invoices-outgoing'] });
    qc.invalidateQueries({ queryKey: ['tax-invoice-outgoing', id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const p = buildPayload();
      if (isNew) return taxInvoicesOutgoing.createManual(p);
      const { organization_id: _o, ...upd } = p;
      return taxInvoicesOutgoing.update(id!, { ...upd, invoice_number: f.invoice_number || undefined });
    },
    onSuccess: (res) => {
      setError(null); invalidate();
      if (isNew) navigate(`/tax-invoices-outgoing/${res.id}`, { replace: true });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const p = buildPayload();
      const doc = isNew
        ? await taxInvoicesOutgoing.createManual(p)
        : await taxInvoicesOutgoing.update(id!, (() => { const { organization_id: _o, ...u } = p; return u; })());
      try {
        return await taxInvoicesOutgoing.post(doc.id);
      } catch (e) {
        // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
        if (isNew) navigate(`/tax-invoices-outgoing/${doc.id}`, { replace: true });
        throw e;
      }
    },
    onSuccess: (res) => {
      setError(null); invalidate();
      navigate(`/tax-invoices-outgoing/${res.id}`, { replace: true });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const unpost = useMutation({
    mutationFn: () => taxInvoicesOutgoing.unpost(id!),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });
  const remove = useMutation({
    mutationFn: () => taxInvoicesOutgoing.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-invoices-outgoing'] });
      navigate('/tax-invoices-outgoing');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  // ЄРПН — ЄДИНА механіка подання (рішення власника 2026-07-30, як 1ДФ):
  // чернетка вже у Журналі Кабінету ДПС — підпис КЕП і відправка ТАМ.
  async function registerInErpn() {
    if (!id || !d) return;
    setErpnBusy(true); setErpnError(null);
    try {
      const { registered_by_buyer } = await taxInvoicesOutgoing.erpnDraft(id);
      if (registered_by_buyer) {
        // 2026-08-05 (перевірено власником у живому кабінеті): відправка
        // від продавця — ШТАТНИЙ шлях передачі: РК зʼявляється у покупця в
        // Кабінеті ДПС (розділ «Реєстрація ПН-РК»), він підписує і реєструє.
        setErpnWarn(
          'Зменшуючий РК реєструє ПОКУПЕЦЬ: після відправки він зʼявиться у '
          + 'нього в Кабінеті ДПС (розділ «Реєстрація ПН-РК») — покупець '
          + 'підписує і реєструє. Стежте за статусом реєстрації тут.');
      }
      invalidate();
      qc.invalidateQueries({ queryKey: ['dps-submissions'] });
      if (!registered_by_buyer) navigate('/dps-cabinet');
    } catch (e: any) {
      setErpnError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setErpnBusy(false);
    }
  }

  // Реквізити коригованої ПН для посилання «№X від Y» (2026-08-07,
  // зауваження власника). Хук — ДО ранніх return'ів (React #310).
  const correctedId = (d as { corrected_invoice_id?: string | null } | undefined)
    ?.corrected_invoice_id ?? null;
  const correctedQ = useQuery({
    queryKey: ['tax-invoice-outgoing', correctedId],
    queryFn: () => taxInvoicesOutgoing.get(correctedId!),
    enabled: !!correctedId,
  });

  if (!isNew && !d) return <div className="card">Завантаження…</div>;
  if (isNew && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        У вас немає прав створювати ПН у жодній з {totalAccessibleOrgs} ваших організацій.
      </div>
    );
  }

  const isPosted = !!d?.posted;
  const readOnly = isPosted;
  const isCorrection = !!d?.is_correction;
  // ч.1352 (обернений ланцюг «на підставі»): реалізацію можна створити З накладної —
  // накладну зареєстрували раніше, документ обліку оформляють потім. У РК — підстава
  // через виправлювану ПН: повернення від покупця на її реалізацію.
  const baseOpen = (type: string | null | undefined, bid: string | null | undefined) => {
    const to = type && bid ? DOC_ROUTES[type]?.(bid) : undefined;
    if (to) navigate(to);
  };
  const reverseActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] =
    isNew ? [] : isCorrection ? [(() => {
      const parentSale = parentPnQ.data?.base_document_type === 'doc.goods_sales'
        ? parentPnQ.data.base_document_id : null;
      return {
        label: 'створити повернення від покупця',
        title: !parentSale
          ? 'У виправлюваної ПН немає реалізації-підстави — спершу привʼяжіть її там'
          : 'Повернення від покупця на реалізацію виправлюваної ПН (рядки — залишок до повернення)',
        disabled: !parentSale,
        onClick: () => navigate(`/goods-returns-from-customer/new?basis=${parentSale}`),
      };
    })()] : [
      ...(d?.base_document_id ? [{
        label: 'відкрити документ-підставу',
        title: 'Реалізація (акт), на підставі якої складено цю ПН',
        onClick: () => baseOpen(d.base_document_type, d.base_document_id),
      }] : [{
        label: 'створити реалізацію',
        title: 'Документ реалізації з рядків цієї ПН; накладна стане його податковою накладною',
        onClick: () => navigate(`/goods-sales/new?tax_invoice=${id}`),
      }]),
      // ч.1359: з ПН — і рахунок, і замовлення покупця (рядки з накладної; звʼязок у «Звʼязаних»).
      { label: 'створити рахунок', title: 'Рахунок покупцю з рядків цієї ПН',
        onClick: () => navigate(`/customer-invoices/new?tax_invoice=${id}`) },
      { label: 'створити замовлення покупця', title: 'Замовлення покупця з рядків цієї ПН',
        onClick: () => navigate(`/customer-orders/new?tax_invoice=${id}`) },
    ];

  // ч.889/898: «Повʼязані дії» — ручна позначка реєстрації в ЄРПН (для ПН,
  // зареєстрованих поза сервісом: мігровані з 1С, Медок до переходу) і її зняття.
  const manualMark = !!d && d.erpn_status === 'REGISTERED'
    && (d.erpn_response ?? '').startsWith('Позначено зареєстрованою вручну');
  const inFlight = !!d && ['DRAFT', 'SENDING', 'SENT'].includes(d.erpn_status);
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] =
    [...reverseActions, ...(manualMark ? [{
      label: 'зняти позначку реєстрації',
      title: 'Повернути «не відправлено» (лише для ручної позначки)',
      onClick: async () => {
        if (!(await confirm('Зняти ручну позначку реєстрації в ЄРПН?'))) return;
        setMarkBusy(true);
        try { await taxInvoicesOutgoing.erpnUnmarkRegistered(id!); invalidate(); }
        finally { setMarkBusy(false); }
      },
    }] : [{
      label: 'позначити зареєстрованою в ЄРПН',
      title: isNew ? 'Спершу збережіть документ'
        : !isPosted ? 'Спершу проведіть ПН'
        : d?.erpn_status === 'REGISTERED' ? 'ПН уже зареєстрована (за квитанцією ДПС)'
        : inFlight ? 'Подача у Кабінеті ДПС триває — дочекайтесь квитанції'
        : 'ПН зареєстровано поза сервісом (Медок, 1С до переходу) — проставити статус і дату вручну',
      disabled: isNew || !isPosted || d?.erpn_status === 'REGISTERED' || inFlight || markBusy,
      onClick: () => setMarkOpen(true),
    }])];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (f.organization_id ? 1 : 0) + (f.invoice_date ? 1 : 0) },
    { id: 'party', title: 'Покупець і підстава', required: 1, filled: f.contractor_id ? 1 : 0 },
    { id: 'items', title: 'Рядки', required: 1, filled: f.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова податкова накладна'
            : `${isCorrection ? 'РК' : 'ПН'} №${d!.invoice_number} від ${new Date(d!.invoice_date).toLocaleDateString('uk-UA')}`}
        </h2>
        <div style={{ marginLeft: 'auto' }}>
          <DocumentToolbar
            entriesDoc={{ type: 'tax.tax_invoices_outgoing', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
            isNew={isNew}
            onBackToList={() => navigate('/tax-invoices-outgoing')}
            onSave={() => save.mutate()}
            saveDisabled={readOnly}
            savePending={save.isPending}
            isPosted={isPosted}
            onPost={() => saveAndPost.mutate()}
            onUnpost={() => unpost.mutate()}
            postPending={saveAndPost.isPending}
            unpostPending={unpost.isPending}
            postTitle="Зберігає та проводить: податкове зобов'язання у регістр ПДВ"
            onPrint={!isNew ? () => navigate(`/tax-invoices-outgoing/${id}/print`) : undefined}
            deletable={!isPosted && !isNew}
            onDelete={() => {
              confirm('Видалити ПН?').then((ok) => { if (ok) remove.mutate(); });
            }}
          />
        </div>
      </div>


      {error && (
        <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      {/* Статус ЄРПН — єдиний рядок-банер (еталон J05). */}
      {!isNew && d && (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
          color: 'var(--badge-blue-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
          marginBottom: '0.75rem', display: 'flex', gap: '0.5rem',
          alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {d.erpn_status === 'REGISTERED'
              ? `✓ зареєстровано в ЄРПН${d.erpn_registration_date
                ? ` від ${new Date(d.erpn_registration_date).toLocaleDateString('uk-UA')}` : ''}`
              : d.erpn_status === 'REJECTED' ? '✗ відмовлено у реєстрації'
              : d.erpn_status === 'STOPPED'
                ? '⏸ реєстрацію ЗУПИНЕНО (СМКОР, п.201.16) — подайте пояснення '
                  + 'та копії документів або Таблицю даних платника'
              : d.erpn_status === 'BLOCKED' ? '⏸ реєстрацію заблоковано — див. відповідь ДПС'
              : d.erpn_status === 'SENT'
                ? (isCorrection
                  ? '→ передано — очікує підпису і реєстрації ПОКУПЦЕМ (його Кабінет ДПС → «Реєстрація ПН-РК»)'
                  : '→ відправлено на реєстрацію, обробляється')
              : d.erpn_status === 'DRAFT'
                ? 'чернетка подачі у Кабінеті ДПС — підпишіть і відправте там'
              : d.erpn_status === 'ERROR' ? '✗ помилка відправки — див. Кабінет ДПС'
              : isPosted
                ? 'ще не передано на реєстрацію в ЄРПН'
                : 'проведіть ПН — тоді можна передати на реєстрацію в ЄРПН'}
          </span>
          {d.erpn_status === 'NOT_SENT' && isPosted ? (
            <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
              onClick={() => { if (!erpnBusy) void registerInErpn(); }}>
              {erpnBusy ? 'готується…' : 'передати у Кабінет ДПС →'}
            </a>
          ) : (<>
            {/* ч.1296: зупинено → пояснення та копії документів; відмовлено → скарга. */}
            {(d.erpn_status === 'STOPPED' || d.erpn_status === 'BLOCKED') && (
              <a style={{ cursor: 'pointer', marginLeft: 'auto', fontWeight: 600 }}
                onClick={() => navigate(`/erpn-appeals/new?invoice=${id}&kind=EXPLANATION`)}>подати пояснення →</a>
            )}
            {d.erpn_status === 'REJECTED' && (
              <a style={{ cursor: 'pointer', marginLeft: 'auto', fontWeight: 600 }}
                onClick={() => navigate(`/erpn-appeals/new?invoice=${id}&kind=COMPLAINT`)}>подати скаргу →</a>
            )}
            <a style={{ cursor: 'pointer', marginLeft: ['STOPPED', 'BLOCKED', 'REJECTED'].includes(d.erpn_status) ? undefined : 'auto' }}
              onClick={() => navigate('/dps-cabinet')}>Кабінет ДПС →</a>
          </>)}
        </div>
      )}
      {markOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setMarkOpen(false)}>
          <div className="card" style={{ width: 420, margin: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Зареєстровано в ЄРПН поза сервісом</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setMarkOpen(false)}>закрити</button>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
              Квитанції ДПС у сервісі не буде — статус проставляється вручну з відміткою
              для аудиту. Використовуйте для ПН, зареєстрованих через Медок/1С до переходу.
            </p>
            <div className="field" style={{ maxWidth: 200 }}>
              <label>Дата реєстрації в ЄРПН</label>
              <DatePopover value={markDate} onChange={(v) => setMarkDate(v)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button onClick={() => setMarkOpen(false)}>скасувати</button>
              <button className="primary" disabled={!markDate || markBusy}
                onClick={async () => {
                  setMarkBusy(true);
                  try {
                    await taxInvoicesOutgoing.erpnMarkRegistered(id!, markDate);
                    invalidate(); setMarkOpen(false);
                  } finally { setMarkBusy(false); }
                }}>
                позначити зареєстрованою
              </button>
            </div>
          </div>
        </div>
      )}
      {erpnWarn && (
        <div style={{
          marginBottom: 8, color: 'var(--badge-amber-fg)', background: 'var(--badge-amber-bg)',
          border: '1px solid var(--badge-amber-bd)', borderRadius: 6, padding: '0.5rem 0.75rem',
        }}>
          ⚠ {erpnWarn}
        </div>
      )}
      {erpnError && (
        <div style={{ marginBottom: 8, color: '#dc2626', whiteSpace: 'pre-wrap' }}>
          {erpnError}
        </div>
      )}
      {!isNew && (d as any)?.erpn_response && (
        <div style={{ marginBottom: 8, color: '#333', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
          Відповідь ДПС: {(d as any).erpn_response}
        </div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'tax.tax_invoices_outgoing', id: isNew ? '' : id! }}
          extraCards={!isNew && d ? <ErpnAppealsCard invoiceId={id!} erpnStatus={d.erpn_status} /> : undefined} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата складання *</label>
          <DatePopover value={f.invoice_date} disabled={readOnly}
            onChange={(v) => setF((s) => ({ ...s, invoice_date: v }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Організація (продавець) *</label>
          <select value={f.organization_id} disabled={!isNew}
            onChange={(e) => setF((s) => ({
              ...s, organization_id: e.target.value, contract_id: '',
            }))}>
            <option value="">— оберіть —</option>
            {(isNew ? availableOrgs : (orgListQ.data?.items ?? [])).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{isCorrection ? '№ РК' : '№ ПН'}</label>
          <input value={f.invoice_number} placeholder="авто" disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, invoice_number: e.target.value }))} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Покупець і підстава">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}>
        <div className="field">
          <label>
            Покупець *
            {buyer && (
              <span style={{ marginLeft: 8, color: 'var(--text-secondary)', fontWeight: 400 }}>
                {buyer.vat_number ? `ІПН ${buyer.vat_number}` : 'без ІПН (неплатник?)'}
                {buyer.edrpou ? ` · ЄДРПОУ ${buyer.edrpou}` : ''}
              </span>
            )}
          </label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou,
            }))}
            value={f.contractor_id}
            disabled={readOnly || isCorrection || !!f.base_sale_id}
            onChange={(cid) => setF((s) => ({ ...s, contractor_id: cid, contract_id: '' }))}
          />
        </div>
        <div className="field">
          <label>Договір</label>
          <select value={f.contract_id}
            disabled={readOnly || !f.contractor_id || !!f.base_sale_id}
            onChange={(e) => setF((s) => ({ ...s, contract_id: e.target.value }))}>
            <option value="">без договору</option>
            {(contractsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {!isCorrection && (
          <div className="field">
            {/* 2026-07-31 (рішення власника): поки звʼязок є — Покупець/Договір
                неактивні; ч.889 — канон підстави ч.861: підпис «Підстава» лише
                при звʼязку, кнопка — лише поки звʼязку немає, × знімає. */}
            <label>{f.base_sale_id ? 'Підстава' : '\u00A0'}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {!f.base_sale_id && (
              <button type="button" className="btn-doc" disabled={readOnly}
                onClick={() => setSalePopup(true)}>
                <DocIcon />заповнити з підстави
              </button>
              )}
              {f.base_sale_id && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => baseOpen(f.base_doc_type, f.base_sale_id)}>
                    {f.base_doc_type === 'doc.production_service_acts'
                      ? 'акт послуг'
                      : f.base_doc_type === 'doc.processing_services_sales'
                        ? 'послуги переробки'
                        : isReceiptBase(f.base_doc_type)
                          ? `${f.base_doc_type === 'doc.bank_receipts' ? 'надходження на рахунок' : 'ПКО'} ${baseReceiptQ.data
                              ? `№${baseReceiptQ.data.document_number} від ${new Date(baseReceiptQ.data.document_date).toLocaleDateString('uk-UA')}`
                              : ''}`
                          : `реалізація ${baseSaleQ.data
                              ? `№${baseSaleQ.data.document_number} від ${new Date(baseSaleQ.data.document_date).toLocaleDateString('uk-UA')}`
                              : ''}`}
                  </a>
                  {/* ч.1447: ПН на аванс тримається за свою оплату — звʼязок не знімається. */}
                  {!readOnly && !isReceiptBase(f.base_doc_type) && (
                    <button type="button" className="btn-x"
                      onClick={() => setF((s) => ({ ...s, base_sale_id: '', items: [] }))}
                      title="Зняти звʼязок з реалізацією і очистити рядки">×</button>
                  )}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {isCorrection && correctedId && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '0.1rem 0 0.4rem' }}>
          РК до ПН: {' '}
          <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
            title="Відкрити кориговану ПН"
            onClick={() => navigate(`/tax-invoices-outgoing/${correctedId}`)}>
            {correctedQ.data
              ? `№${correctedQ.data.invoice_number} від ${new Date(correctedQ.data.invoice_date).toLocaleDateString('uk-UA')}`
              : 'відкрити кориговану ПН'}
          </a>
        </div>
      )}
      {!isCorrection && (
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', margin: '0.25rem 0 0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <input type="checkbox" checked={f.not_issued} disabled={readOnly}
              style={{ width: 'auto' }}
              onChange={(e) => setF((s) => ({ ...s, not_issued: e.target.checked }))} />
            не видається покупцю
          </label>
          {f.not_issued && (
            <select value={f.not_issued_reason} disabled={readOnly}
              style={{ maxWidth: 460 }}
              onChange={(e) => setF((s) => ({ ...s, not_issued_reason: e.target.value }))}>
              {NOT_ISSUED_REASONS.map(([code, name]) => (
                <option key={code} value={code}>{code} — {name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      </DocBlock>

      <DocBlock index={3} title="Рядки"
        actions={!readOnly ? (<>
          <button type="button" onClick={addRow}>+ порожній рядок</button>
          <button type="button" className="primary"
            onClick={() => setPickerOpen(true)}
            disabled={!f.organization_id}
            title={!f.organization_id
              ? 'Спершу оберіть організацію'
              : 'Підбір товару/послуги з довідника номенклатури'}>
            + додати
          </button>
        </>) : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          {([['GOODS', 'Товари'], ['SERVICE', 'Послуги'], ['ASSET', 'ОЗ і НМА']] as const).map(([k, l]) => (
            <button key={k} type="button" className={linesTab === k ? 'active' : ''}
              onClick={() => setLinesTab(k)}>
              {l} ({f.items.filter((r) => rowTab(r) === k).length})
            </button>
          ))}
        </div>
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th style={{ minWidth: 180 }}>Номенклатура</th>
            <th style={{ minWidth: 160 }}>Опис (у ПН)</th>
            <th style={{ width: 110 }}>УКТЗЕД</th>
            <th style={{ width: 85, textAlign: 'right' }}>К-сть</th>
            <th style={{ width: 100, textAlign: 'right' }}>Ціна без ПДВ</th>
            <th style={{ width: 95 }}>Ставка</th>
            <th style={{ width: 95, textAlign: 'right' }}>База</th>
            <th style={{ width: 90, textAlign: 'right' }}>ПДВ</th>
            {!readOnly && <th style={{ width: 30 }}></th>}
          </tr>
        </thead>
        <tbody>
          {f.items.map((r, i) => {
            if (rowTab(r) !== linesTab) return null;
            const { base, vat } = lineAmounts(r);
            return (
              <tr key={r._key}>
                <td>{i + 1}</td>
                <td>
                  <NomenclatureSelect value={r.nomenclature_id ?? ''} disabled={readOnly}
                    onChange={(nid) => setRow(r._key, {
                      nomenclature_id: nid || null,
                      description: r.description || (nomById.get(nid)?.name ?? ''),
                      ukt_zed_code: r.ukt_zed_code || (nomById.get(nid)?.ukt ?? null),
                    })} />
                </td>
                <td>
                  <input value={r.description} disabled={readOnly}
                    onChange={(e) => setRow(r._key, { description: e.target.value })} />
                </td>
                <td>
                  <input value={r.ukt_zed_code ?? ''} disabled={readOnly}
                    placeholder="для товарів"
                    onChange={(e) => setRow(r._key, { ukt_zed_code: e.target.value || null })} />
                </td>
                <td>
                  <input type="number" min="0" step="0.001" value={r.quantity}
                    disabled={readOnly} style={{ textAlign: 'right' }}
                    onChange={(e) => setRow(r._key, { quantity: e.target.value })} />
                </td>
                <td>
                  <input type="number" min="0" step="0.01" value={r.price_without_vat}
                    disabled={readOnly} style={{ textAlign: 'right' }}
                    onChange={(e) => setRow(r._key, { price_without_vat: e.target.value })} />
                </td>
                <td>
                  <select value={r.vat_rate_id} disabled={readOnly}
                    onChange={(e) => setRow(r._key, { vat_rate_id: e.target.value })}>
                    {rates.map((vr) => (
                      <option key={vr.id} value={vr.id}>{vr.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {/* ч.891: підсумковий рядок (кількість 0) — база редагується прямо. */}
                  {!readOnly && (Number(r.quantity) || 0) === 0 ? (
                    <input type="number" min="0" step="0.01" value={r.base_amount ?? ''}
                      style={{ textAlign: 'right' }} placeholder="сума"
                      onChange={(e) => setRow(r._key, { base_amount: e.target.value, vat_amount: null })} />
                  ) : fmt(base)}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(vat)}</td>
                {!readOnly && (
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ cursor: 'pointer', color: '#dc2626' }}
                      title="прибрати рядок"
                      onClick={() => setF((s) => ({
                        ...s, items: s.items.filter((x) => x._key !== r._key),
                      }))}>×</span>
                  </td>
                )}
              </tr>
            );
          })}
          {f.items.length === 0 && (
            <tr><td colSpan={10} style={{ color: 'var(--text-secondary)' }}>
              Немає рядків — додайте вручну або «заповнити з реалізації».
            </td></tr>
          )}
        </tbody>
      </TchTable>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <table className="tch" style={{ width: 'auto', minWidth: 320 }}>
          <tbody>
            {totals.b20 > 0 && (
              <tr><td>20%: база {fmt(totals.b20)}</td>
                <td style={{ textAlign: 'right' }}>ПДВ {fmt(totals.v20)}</td></tr>
            )}
            {totals.b14 > 0 && (
              <tr><td>14%: база {fmt(totals.b14)}</td>
                <td style={{ textAlign: 'right' }}>ПДВ {fmt(totals.v14)}</td></tr>
            )}
            {totals.b7 > 0 && (
              <tr><td>7%: база {fmt(totals.b7)}</td>
                <td style={{ textAlign: 'right' }}>ПДВ {fmt(totals.v7)}</td></tr>
            )}
            {totals.b0 > 0 && (
              <tr><td>0%</td><td style={{ textAlign: 'right' }}>{fmt(totals.b0)}</td></tr>
            )}
            {totals.bex > 0 && (
              <tr><td>Звільнено / не об'єкт</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.bex)}</td></tr>
            )}
            <tr style={{ fontWeight: 600, fontSize: '1rem' }}>
              <td>Усього з ПДВ</td>
              <td style={{ textAlign: 'right' }}>{fmt(totals.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {pickerOpen && f.organization_id && (
        <NomenclaturePickerModal
          organizationId={f.organization_id}
          documentDate={f.invoice_date}
          onPick={addRowFromPicker}
          onClose={() => setPickerOpen(false)}
          catalogMode
        />
      )}

      {salePopup && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60,
        }} onClick={() => setSalePopup(false)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem',
            width: 720, maxHeight: '75vh', overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Оберіть підставу (реалізація / акт послуг / послуги переробки)</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setSalePopup(false)}>закрити</button>
            </div>
            {(salesQ.isLoading || actsQ.isLoading || pssQ.isLoading) && <div>Завантаження…</div>}
            {(() => {
              // ч.697: підстава ПН — реалізація АБО акт послуг АБО послуги
              // переробки (обʼєднаний список, колонка «Тип»).
              const rows = [
                ...(salesQ.data?.items ?? []).map((x) => ({
                  id: x.id, num: x.document_number, date: x.document_date,
                  total: x.total_amount, kind: 'Реалізація',
                  pick: () => fillFromSale(x.id),
                })),
                ...(actsQ.data?.items ?? []).map((x) => ({
                  id: x.id, num: x.document_number, date: x.document_date,
                  total: x.total_amount, kind: 'Акт послуг',
                  pick: () => fillFromAct(x.id),
                })),
                ...(pssQ.data?.items ?? []).map((x) => ({
                  id: x.id, num: x.document_number, date: x.document_date,
                  total: x.total_amount, kind: 'Послуги переробки',
                  pick: () => fillFromPss(x.id),
                })),
              ].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
              if (!salesQ.isLoading && !actsQ.isLoading && !pssQ.isLoading && rows.length === 0) {
                return (
                  <div style={{ color: 'var(--text-secondary)' }}>
                    Немає проведених реалізацій, актів послуг чи послуг переробки.
                  </div>
                );
              }
              if (rows.length === 0) return null;
              return (
                <TchTable>
                  <thead>
                    <tr>
                      <th>№</th>
                      <th style={{ width: 140 }}>Тип</th>
                      <th>Дата</th>
                      <th style={{ textAlign: 'right' }}>Сума</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} style={{ cursor: 'pointer' }}
                        onDoubleClick={r.pick}>
                        <td>{r.num}</td>
                        <td>{r.kind}</td>
                        <td>
                          {r.date ? new Date(r.date).toLocaleDateString('uk-UA') : ''}
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmt(r.total)}</td>
                        <td>
                          <button onClick={r.pick}>обрати</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TchTable>
              );
            })()}
          </div>
        </div>
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={f.comment} disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, comment: e.target.value }))} />
        </div>
        <div>
          {/* Вкладення (рішення власника 2026-08-26). */}
          <AttachmentsPanel organizationId={f.organization_id}
            documentType={'tax.tax_invoices_outgoing'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
